
import { defineComponent, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { AuthController } from "@/app/ui/controllers/AuthController";
import Footer from "@/app/ui/components/footer/index.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { cleanPhoneNumber } from "@/app/infrastructures/misc/common-library/Whatsapp";
import HelpCenter from "./help-center.vue";
import { SetupPasswordRequest } from "@/data/payload/api/AuthRequest";
import { createI18n } from "vue-i18n";
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";

export default defineComponent({
  name: "SetupPassword",
  components: {
    Footer,
    HelpCenter
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const i18nInstance = createI18n({
      legacy: false,
      locale: router.currentRoute.value.query?.is_en === "true" ? "my" : "id",
      fallbackLocale: "id",
      globalInjection: false,
      messages: {
        en,
        id,
        my
      }
    });
    const t = i18nInstance.global.t;

    // state
    const newPassword = ref("");
    const confirmPassword = ref("");
    const passwordLength = ref(0);
    const containsEightCharacters = ref(false);
    const containsNumber = ref(false);
    const containsUppercase = ref(false);
    const containsGenesis = ref(false);
    const validPassword = ref(false);
    const showModalError = ref(false);

    const newPasswordType = ref<"password" | "text">("password");
    const confirmPasswordType = ref<"password" | "text">("password");
    const iconType = ref("eye-slash");
    const iconTypeConfirm = ref("eye-slash");

    // computed
    const token = computed(() => String(route.query?.token || ""));
    const email = computed(() => String(route.query?.email || ""));
    const openModalSuccess = computed(() => AuthController.openModalSuccess);
    const showModal = computed(
      () => openModalSuccess.value || showModalError.value
    );
    const toLogin = () => {
      router.push("/login");
    };
    const onClose = () => {
      AuthController.setModalSuccess(false);
      toLogin();
    };

    const onCloseErrorModal = () => {
      showModalError.value = false;
    };
    const modalData = computed(() => {
      if (openModalSuccess.value) {
        return {
          title: t("setupPassword.success.title"),
          message: t("setupPassword.success.message"),
          image: "badge-success",
          textSuccess: t("setupPassword.success.button"),
          onSubmit: onClose
        };
      } else if (showModalError.value) {
        return {
          title: t("setupPassword.error.title"),
          message: t("setupPassword.error.message"),
          image: "ill_badge_failed",
          textSuccess: t("setupPassword.error.button"),
          onSubmit: onCloseErrorModal
        };
      } else {
        return {} as any;
      }
    });

    // methods
    const onSetupPassword = async () => {
      try {
        MainAppController.showLoading();

        const payload = new SetupPasswordRequest({
          token: token.value,
          email: email.value,
          password: newPassword.value
        });

        await AuthController.setupPassword(payload);
        AuthController.setModalSuccess(true);
      } catch (error) {
        showModalError.value = true;
      } finally {
        MainAppController.closeLoading();
      }
    };

    const showNewPassword = () => {
      if (
        newPasswordType.value === "password" &&
        iconType.value === "eye-slash"
      ) {
        newPasswordType.value = "text";
        iconType.value = "eye";
      } else {
        newPasswordType.value = "password";
        iconType.value = "eye-slash";
      }
    };

    const showConfirmPassword = () => {
      if (
        confirmPasswordType.value === "password" &&
        iconTypeConfirm.value === "eye-slash"
      ) {
        confirmPasswordType.value = "text";
        iconTypeConfirm.value = "eye";
      } else {
        confirmPasswordType.value = "password";
        iconTypeConfirm.value = "eye-slash";
      }
    };

    const checkPassword = () => {
      passwordLength.value = newPassword.value.length;
      containsEightCharacters.value = passwordLength.value >= 8;
      containsNumber.value = /\d/.test(newPassword.value);
      containsUppercase.value = /[A-Z]/.test(newPassword.value);
      containsGenesis.value = newPassword.value
        .toLowerCase()
        .includes("genesis");
      validPassword.value =
        containsEightCharacters.value &&
        containsNumber.value &&
        containsUppercase.value &&
        newPassword.value === confirmPassword.value;
    };

    // error modal
    const loadingModal = computed(() => MainAppController.loadingModal);
    const errorModal = computed(() => MainAppController.errorMessageModal);
    const errorEntities = computed(
      () => MainAppController.errorMessageEntities
    );

    const errorTextCancel = computed(() =>
      errorEntities.value.onTryAgain ? t("resetPasswordPage.back") : ""
    );
    const errorTextSuccess = computed(() =>
      errorEntities.value.onTryAgain ? t("resetPasswordPage.tryAgain") : t("resetPasswordPage.ok")
    );

    const closeErrorModal = () => {
      errorEntities.value.onClose?.();
      MainAppController.closeErrorMessage();
    };

    const errorOnSubmit = computed(() =>
      errorEntities.value.onTryAgain
        ? errorEntities.value.onTryAgain
        : closeErrorModal
    );

    const disabled = computed(
      () =>
        !validPassword.value ||
        !containsEightCharacters.value ||
        !containsNumber.value ||
        !containsUppercase.value ||
        containsGenesis.value
    );

    return {
      // state
      newPassword,
      confirmPassword,
      passwordLength,
      containsEightCharacters,
      containsNumber,
      containsUppercase,
      containsGenesis,
      validPassword,
      newPasswordType,
      confirmPasswordType,
      iconType,
      iconTypeConfirm,
      cleanPhoneNumber,
      t,

      // computed
      token,
      email,
      openModalSuccess,
      loadingModal,
      errorModal,
      errorEntities,
      errorTextCancel,
      errorTextSuccess,
      errorOnSubmit,
      disabled,
      showModalError,
      modalData,
      showModal,

      // methods
      onSetupPassword,
      onClose,
      toLogin,
      showNewPassword,
      showConfirmPassword,
      checkPassword,
      closeErrorModal,
      onCloseErrorModal
    };
  }
});
